import { Component, Element, Host, h, Prop } from "@stencil/core";

/**
 * @slot title - Slot for the headline (`<h2>` or `<h3>`)
 * @slot subtitle - Slot for the subtitle (`<p>` or `<div>`)
 * @slot description - Slot for the description text (`<p>` or `<div>`)
 */
@Component({
    tag: "ux-iws-nb-chapter-intro",
    styleUrl: "nb-chapter-intro.scss",
    shadow: false,
})
export class NbChapterIntro {
    private static uidIncrement = 0;
    private uid = `nb-chapter-intro-${NbChapterIntro.uidIncrement++}`;
    private headingLevel: number = 2;

    /**
     * The element reference for the component.
     * Used to access the DOM and manipulate styles or classes.
     */
    @Element() host: HTMLUxIwsNbChapterIntroElement;

    /** Label of the chapter */
    @Prop() chapter: string = "";
    /** If set, a horizontal separator line is drawn */
    @Prop({ reflect: true }) separator: boolean = false;
    /** Comma separated list of tags */
    @Prop() tags: string = "";
    /** Label for the list of supported tags (accessibility) */
    @Prop() tagsLabel: string = "Supported Applications";

    private detectHeadingLevel(): number {
        const titleSlot = this.host.querySelector("[slot='title']");
        const headingElement =
            titleSlot.querySelector("h1, h2, h3") || titleSlot;
        let headlingLevel = parseInt(headingElement.nodeName.replace("H", ""));
        if (
            Number.isNaN(headlingLevel) ||
            headlingLevel < 1 ||
            headlingLevel > 3
        ) {
            headlingLevel = 2;
        }
        return headlingLevel;
    }

    componentWillLoad() {
        this.headingLevel = this.detectHeadingLevel();
    }

    render() {
        const baseClass = "ux-iws-nb-chapter-intro";
        const allTags = this.tags
            .split(",")
            .map((tag) => tag.trim())
            .filter((tag) => tag.length > 0);
        const SubHeadingTag = `h${this.headingLevel + 1}`;
        return (
            <Host
                class={{
                    [`${baseClass}`]: true,
                }}
            >
                {this.chapter && (
                    <div class={`${baseClass}__chapter`}>
                        <span class={`${baseClass}__chapter-label`}>
                            {this.chapter}
                        </span>
                    </div>
                )}
                <div class={`${baseClass}__inner`}>
                    <header class={`${baseClass}__header`}>
                        {allTags.length > 0 && (
                            <section
                                class={`${baseClass}__tags`}
                                aria-labelledby={`${this.uid}-tags-label`}
                            >
                                <SubHeadingTag
                                    id={`${this.uid}-tags-label`}
                                    class="visually-hidden"
                                >
                                    {this.tagsLabel}
                                </SubHeadingTag>
                                {allTags.map((tag) => (
                                    <span
                                        class={{
                                            [`${baseClass}__tag`]: true,
                                        }}
                                        key={tag}
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </section>
                        )}
                        <div class={`${baseClass}__title`}>
                            <slot name="title" />
                            <slot name="subtitle" />
                        </div>
                        <div class={`${baseClass}__text`}>
                            <slot name="description" />
                        </div>
                    </header>
                </div>
            </Host>
        );
    }
}
